// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';

import EmailLabelLink from './../../components/EmailLabelLink';
import { ILineItem } from './../../businessLogic/sagas/serviceRequest';
import { actionStatusSelector, LoadingState } from './../../store/actionStatus';
import { GET_SHIPPING_LABEL, getShippingLabel, regenerateShippingLabel, putReconcileShippingLabel, getReconcileUsers } from './../../store/deviceInfo';

import { logPageAction } from 'src/lib/ApplicationInsights';

interface PropType {
    claimOrderId: string;
    lineItem: ILineItem;
    serialNumber: string;
    serviceRequestNumber: number;
    status: string;
}

export default function ShippingLabelLink (props: PropType) {
    const { t } = useTranslation();
    const {
        claimOrderId,
        lineItem,
        serviceRequestNumber,
        status,
    } = props;
    const dispatch = useDispatch();

    const lineItemId = lineItem.lineItemId;
    const actionName = `${GET_SHIPPING_LABEL}-${lineItemId}`;
    const actionStatus = useSelector((state: any) => actionStatusSelector(state)(actionName));
    const shippingLabel = useSelector((state: any) => {
        const labelsByLineItem = state.device.shippingLabelByLineItem || {};
        return labelsByLineItem[lineItemId] || state.device.shippingLabel || {};
    });
    const reconcileUsers = useSelector((state: any) => state.device.reconcileUsers || []);
    const loggedInUser = useSelector((state: any) => state.userInfo || {});
    const reconcileShippinglabel = useSelector((state: any) => state.device.reconcileShippinglabel);

    function onClick (e: React.MouseEvent) {
        logPageAction('View or Print shipping label link clicked');
        
        OpenShippingLabel(shippingLabel.shippingLabelData);
        e.stopPropagation();
    }

    function fetchShippingLabel (e?: React.MouseEvent) {
        e?.preventDefault();
        logPageAction('Shipping label link clicked');
        dispatch(getShippingLabel({ claimOrderId, lineItemId }));
        if (e && e.stopPropagation) e.stopPropagation();
    }
    function regenerateLabel(e: React.MouseEvent) {
        logPageAction('Regenerate Shipping label link clicked');
        dispatch(regenerateShippingLabel({ claimOrderId, lineItemId }));
        e.stopPropagation();
    }
    function reconcileLabel(e: React.MouseEvent) {
        e.preventDefault();
        logPageAction('Reconcile Shipping label link clicked');
        dispatch(putReconcileShippingLabel(serviceRequestNumber));
        e.stopPropagation();
    }
    const today = new Date().getTime();
    const expiryDate = lineItem.inboundLabelInfo && lineItem.inboundLabelInfo.expirationDateTime ? new Date(lineItem.inboundLabelInfo.expirationDateTime).getTime() : null;
    const isExpiryDate = expiryDate && today > expiryDate;
    const isServiceProcessComplete = lineItem && lineItem.progress &&
    lineItem.progress.states &&
    lineItem.progress.states.some(el => (el.eventTrigger === 'nt_autocomplete' || el.eventTrigger === 'nt_completed')
    && el.isComplete );
    React.useEffect(() => {
        if (
            actionStatus.status === LoadingState.notStarted
            && status === 'open'
            && lineItem.inboundLabelInfo
            && !isExpiryDate
        ) {
            fetchShippingLabel();
        }
    },[lineItemId]);
    React.useEffect(() => {
        if (
            actionStatus.status === LoadingState.rejected
            && status === 'open'
            && lineItem.inboundLabelInfo
            && !isExpiryDate
        ) {
            fetchShippingLabel();
        }
    },[reconcileShippinglabel]);
    React.useEffect(() => {
        dispatch(getReconcileUsers());
    },[]);
    switch (actionStatus.status) {
        case LoadingState.success:
            return (
                shippingLabel && shippingLabel.shippingLabelData ?
                    (<div>
                        <div>
                            <a href="javascript:;" onClick={onClick}>
                                {t('view_or_print_your_shipping_label')}
                            </a>
                        </div>
                        <EmailLabelLink
                            serviceRequestNumber={serviceRequestNumber}
                            serialNumber={lineItem.serialNumber}
                        />
                    </div>) :
                    (<div>
                       {t('label_generated')}
                    </div>)
            );
        case LoadingState.notStarted:
            return (
                isExpiryDate && status === 'open' && !isServiceProcessComplete? (
                    <div>
                        <div>
                            <a href="javascript:;" onClick={regenerateLabel}>
                            {t('recreate_shipping_label')}
                            </a>
                        </div>
                    </div>
                ) : (
                    <div>{t('shipping_label_not_available')}</div>
                )
            );
        case LoadingState.rejected:
            return (
                <div>
                    <div>{t('shipping_label_not_available')}</div>
                    {isExpiryDate ? (
                        <a onClick={regenerateLabel} href="javascript:;">{t('try_again')}</a>
                    ): (
                        <>
                            <a onClick={fetchShippingLabel} href="#">{t('try_again')}</a>
                            {
                                reconcileUsers &&  loggedInUser && reconcileUsers.includes(loggedInUser.userInfo?.email) &&
                                <a onClick={reconcileLabel} href="#" style={{marginLeft:"10px"}}>Reconcile</a>
                                
                            }
                        </>
                    )}
                </div>
            );
        case LoadingState.started:
            return (
                <Spinner
                    size={SpinnerSize.medium}
                />
            );
        default:
            return (
                <div>{t('shipping_label_not_available')}</div>
            );
    }
}

function OpenShippingLabel(data: any){
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {type: 'application/pdf'});

    if ((window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(blob, 'shippingLabel.pdf');
    } else {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    }
}